import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Briefcase, Award, Users } from 'lucide-react';
import './About.css';

const About = () => {
  const highlights = [
    {
      icon: <Award size={24} />,
      label: '20+ Years',
      detail: 'QA & quality engineering leadership',
    },
    {
      icon: <Briefcase size={24} />,
      label: 'Enterprise + Startup',
      detail: 'From fast-moving startups to high-scale industrial tech',
    },
    {
      icon: <Users size={24} />,
      label: 'Small Teams',
      detail: 'Built for companies that can\'t afford a full QA team',
    },
  ];

  const skills = [
    'Playwright & Selenium',
    'CI/CD Integration',
    'Test Strategy & Architecture',
    'AI-Assisted Testing',
    'Risk-Based Testing',
    'Release Process Optimization',
    'Cross-functional QA Leadership',
    'Robotics & Industrial Software QA',
  ];

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="container">
          <motion.div
            className="about-hero-inner"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="about-avatar">
              <div className="about-avatar-circle">
                <img src="/richard-hudson.jpg" alt="Richard Hudson" className="about-avatar-photo" />
              </div>
              <div className="about-avatar-badge">
                <span className="badge-dot"></span>
                Available for new work
              </div>
            </div>

            <div className="about-intro">
              <span className="section-label">About</span>
              <h1>Hi, I'm Rich Hudson.</h1>
              <p className="about-lead">
                I've spent over 20 years in QA and quality engineering — leading
                test teams, designing automation frameworks, and helping companies
                ship software they can be proud of.
              </p>
              <p>
                I'm based in Calgary, AB, and I run Mixality as a side business
                alongside a senior QA leadership role at my day job. That means
                I'm selective about the clients I take on — but when I do, I
                bring my full expertise to the table.
              </p>
              <p>
                My edge is pairing deep QA experience with modern AI tooling to
                deliver in hours what traditionally takes days. Small companies
                finally get access to the kind of quality engineering that's
                usually reserved for large enterprises.
              </p>
              <a
                href="mailto:mrichard.hudson@mixality.com?subject=Free Discovery Call"
                className="btn btn-primary about-cta"
              >
                Book a Free Discovery Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="about-highlights">
        <div className="container">
          <div className="highlights-grid">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                className="highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="highlight-icon">{h.icon}</div>
                <strong>{h.label}</strong>
                <span>{h.detail}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="about-approach">
        <div className="container">
          <div className="about-two-col">
            <motion.div
              className="about-col"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="section-label">My Approach</span>
              <h2>QA That Fits How Small Teams Actually Work</h2>
              <p>
                Most QA engagements are built for big teams with big budgets.
                I do the opposite: I find the smallest intervention that unlocks
                the most velocity for your team.
              </p>
              <p>
                That usually means a focused audit first — understand where your
                biggest risks are — then targeted automation that your devs can
                actually maintain. No overcomplicated frameworks, no shelfware.
              </p>
              <p>
                AI tools let me move faster and spot patterns that would take
                weeks of manual analysis. You get senior-level QA thinking at a
                fraction of the traditional cost.
              </p>
            </motion.div>

            <motion.div
              className="about-col"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Skills & Tools</span>
              <ul className="skills-list">
                {skills.map((s, i) => (
                  <li key={i}>
                    <CheckCircle size={16} className="skill-check" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="about-cta-section">
        <div className="container">
          <motion.div
            className="about-cta-box"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2>Ready to ship with confidence?</h2>
            <p>
              Book a free 20-minute discovery call. We'll look at your current
              setup and see if I'm the right fit.
            </p>
            <a
              href="mailto:mrichard.hudson@mixality.com?subject=Free Discovery Call"
              className="btn btn-primary btn-lg"
            >
              Book a Free Discovery Call
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
